/**
 * AAA-10 + AAA-08: the CENOTAPH hero asset — the hook's money-shot. A 140km hollow
 * nickel-iron memorial sphere carved with the names of the Silence dead.
 *
 * Two-tier asset strategy:
 *  1) HERO GLB (preferred): a high-quality Tripo3D-generated `cenotaph.glb`, loaded
 *     async and normalised to the 140km radius. Used the instant it loads.
 *  2) PROCEDURAL FALLBACK: a baked-inscription sphere (CanvasTexture map+bumpMap,
 *     "texels not geometry" per the perf ruling) so the monument always renders even
 *     before/without the GLB. The carved names use a SEPARATE stonemason serif
 *     (Cinzel, AAA-08) loaded via FontFace — never the UI mono.
 *
 * Placed by floating-origin delta like signals.ts.
 *
 * Distance-based LOD (perf): the hero GLB is toggled via applyLod(); the procedural
 * sphere/ring/motes are NEVER hidden by LOD — they are controlled only by the
 * group.visible flag (set when centre is null).  LOD for the procedural path is
 * intentionally omitted: the sphere is cheap (64×48 geo + canvas texture) and
 * serves as the always-present fallback.
 */
import * as THREE from 'three';
import { cenotaphGlbUrl } from './assetDir.js';
import { createGltfLoader } from './gltf.js';
import { type Vec3 } from '@blackmarble/sim-core';
import { normaliseToRadius } from './bodyFactory.js';
import { applyLod, FULL_CENOTAPH_KM, PROXY_CENOTAPH_KM } from './lod.js';
import {
  CINE_ROT_PHASE,
  CINE_ROT_RATE,
  SUN_DIR,
  clamp,
  easeInOutSine,
  interpolate,
  introRimStrength,
} from '../hud/introTimeline.js';

/** Real Cenotaph radius: 140 km diameter => 70 km radius (1 unit = 1 km). */
const RADIUS_KM = 70;
/** Memorial ring inner/outer radii relative to RADIUS_KM (1.7x .. 2.1x). */
const RING_INNER = RADIUS_KM * 1.7;
const RING_OUTER = RADIUS_KM * 2.1;
/** Ring counter-rotation rate (rad/s) — very slow, reverent. */
const RING_ROT_RATE = -0.004;
/** Candle-motes: sparse drifting particles above the ring plane. */
const MOTE_COUNT = 48;
/** Candle-mote drift rate (rad/s per mote index — deterministic, no Math.random). */
const MOTE_DRIFT_BASE = 0.0007;

const WALL_FONT = 'BM Cinzel';
const FALLBACK = `Georgia, "Times New Roman", serif`;
const INSCRIPTION_SIZE = 4096;
const INSCRIPTION_REPEAT = new THREE.Vector2(12, 6);
const INSCRIPTION_FONT_PX = 40;
const INSCRIPTION_ROW_STEP = 54;
const INSCRIPTION_COL_STEP = 212;
const INSCRIPTION_MARGIN_X = 18;
const INSCRIPTION_MARGIN_Y = 14;
const STONE_METALNESS = 0.62;
const STONE_ROUGHNESS = 0.78;
const STONE_BUMP_SCALE = 0.3;
/** Wave 2b: gameplay stone gains a real tangent-space normal map derived from the
 * height field (Sobel). Cheaper-to-author than a sculpt, sharper relief than bump.
 * Computed at a modest resolution — normals tolerate downsampling — so the one-time
 * derive stays cheap and never janks the (off-limits) intro. */
const NORMAL_SIZE = 512;
const NORMAL_STRENGTH = 40; // Sobel gradient gain — deeper grooves = larger tilt
const STONE_NORMAL_SCALE = 1.1;

// --- Cinematic (intro) matte-obsidian overrides — gated, never touch gameplay. ---
const CINE_METALNESS = 0.0; // pure dielectric obsidian
const CINE_ROUGHNESS = 0.96; // fully matte, no specular hotspot
const CINE_ENVMAP_INTENSITY = 0.0; // zero reflections / bounce
const CINE_ALBEDO = 0x2b3033; // deep cool grey-black — multiplies the bone-grey names DOWN to relief
const CINE_BUMP_SCALE = 0.75; // deepen grooves so the raking key throws legible micro-shadows
/** Foreground paper-ash mote count (sparse, funereal). */
const ASH_COUNT = 34;

/** Stored gameplay material values, restored verbatim when the cinematic flag clears. */
interface CineMatStore {
  metalness: number;
  roughness: number;
  envMapIntensity: number;
  color: number;
  bumpScale: number;
  emissive: number;
  emissiveIntensity: number;
  /** The gameplay normal map, stored so the cinematic can null it (bump-only look,
   * byte-identical to the pre-Wave-2b intro) and restore it on handoff to gameplay. */
  normalMap: THREE.Texture | null;
  onBeforeCompile: THREE.Material['onBeforeCompile'];
}

/**
 * Masked Fresnel rim injected into the cinematic stone material via onBeforeCompile.
 * A grazing directional key on rough matte gives only a dull diffuse terminator; this
 * adds a bounded cold-cyan crescent on the sun-facing limb. It is additive but routed
 * through NO bloom pass — a stone-edge highlight, never a hologram. r183 assembles the
 * frame in `<opaque_fragment>`; `normal`/`vViewPosition`/`outgoingLight` are in scope.
 */
const RIM_GLSL = /* glsl */ `
  {
    vec3 rimN = normalize( normal );
    vec3 rimV = normalize( vViewPosition );
    float rimFres = pow( 1.0 - max( dot( rimN, rimV ), 0.0 ), uRimPower );
    float rimSun = smoothstep( -0.05, 0.40, dot( rimN, uSunDirView ) );
    outgoingLight += uRimColor * rimFres * rimSun * uRimStrength;
  }
`;

const GIVEN_NAMES = [
  'AVERY', 'SANA', 'JOON', 'MARA', 'IDRIS', 'ELIA', 'NOA', 'MIREN', 'TADE', 'INES',
  'NADIA', 'RHEA', 'LEON', 'VEGA', 'OMAR', 'PAVEL', 'LIAN', 'HELEN', 'IRIS', 'ANIK',
] as const;
const FAMILY_NAMES = [
  'OKONKWO', 'TANAKA', 'MEHRAD', 'VELA', 'HALE', 'SORENSEN', 'PARK', 'COLE', 'ALVAREZ', 'MIRZA',
  'KOSTA', 'QI', 'BAPTISTE', 'ISHIKAWA', 'IBRAHIM', 'NOVAK', 'SINGH', 'MENDES', 'RAO', 'KHOURY',
] as const;

interface InscriptionAtlas {
  colorCtx: CanvasRenderingContext2D;
  heightCtx: CanvasRenderingContext2D;
  colorMap: THREE.CanvasTexture;
  heightMap: THREE.CanvasTexture;
  /** Tangent-space normal map derived from the height field (gameplay relief). */
  normalCanvas: HTMLCanvasElement;
  normalMap: THREE.CanvasTexture;
}

/**
 * Derive a tangent-space normal map from a height canvas via a Sobel gradient, at
 * NORMAL_SIZE (downsampled — normals tolerate it, and the source names read fine).
 * Encodes (nx,ny,nz) into RGB with the standard 0.5-biased packing; z is the "up"
 * (blue) channel. `strength` scales the xy gradient (deeper grooves = larger tilt).
 */
const computeNormalMap = (heightCanvas: HTMLCanvasElement, target: HTMLCanvasElement, strength: number): void => {
  const n = target.width;
  const tmp = document.createElement('canvas');
  tmp.width = n;
  tmp.height = n;
  const tctx = tmp.getContext('2d') as CanvasRenderingContext2D;
  tctx.drawImage(heightCanvas, 0, 0, n, n);
  const src = tctx.getImageData(0, 0, n, n).data;
  const out = new ImageData(n, n);
  const h = (x: number, y: number): number =>
    src[(((y + n) % n) * n + ((x + n) % n)) * 4]! / 255;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = (h(x - 1, y) - h(x + 1, y)) * strength;
      const dy = (h(x, y - 1) - h(x, y + 1)) * strength;
      const inv = 1 / Math.hypot(dx, dy, 1);
      const i = (y * n + x) * 4;
      out.data[i] = (dx * inv * 0.5 + 0.5) * 255;
      out.data[i + 1] = (dy * inv * 0.5 + 0.5) * 255;
      out.data[i + 2] = (inv * 0.5 + 0.5) * 255;
      out.data[i + 3] = 255;
    }
  }
  (target.getContext('2d') as CanvasRenderingContext2D).putImageData(out, 0, 0);
};

/** Deterministic hash [0,1) from an integer seed — no Math.random at runtime. */
const hashF = (n: number): number => {
  const s = Math.sin(n * 17.4321) * 43758.5453;
  return s - Math.floor(s);
};

const engravedNameAt = (x: number, y: number): string => {
  const seed = x * 13 + y * 17;
  const given = GIVEN_NAMES[seed % GIVEN_NAMES.length] as string;
  const family = FAMILY_NAMES[(seed * 7) % FAMILY_NAMES.length] as string;
  const id = ((seed * 29) % 90_000) + 10_000;
  return `${given} ${family} ${id}`;
};

const paintStoneBase = (g: CanvasRenderingContext2D, w: number, h: number): void => {
  const wash = g.createLinearGradient(0, 0, w, h);
  wash.addColorStop(0, '#7f878d');
  wash.addColorStop(0.5, '#6c7378');
  wash.addColorStop(1, '#545a5f');
  g.fillStyle = wash;
  g.fillRect(0, 0, w, h);
  for (let i = 0; i < 360; i++) {
    const x = hashF(i * 3 + 0) * w;
    const y = hashF(i * 3 + 1) * h;
    const len = 60 + hashF(i * 3 + 2) * 180;
    g.fillStyle = `rgba(255,255,255,${0.018 + hashF(i + 9) * 0.018})`;
    g.fillRect(x, y, len, 1);
    g.fillStyle = `rgba(18,20,22,${0.02 + hashF(i + 27) * 0.03})`;
    g.fillRect(x * 0.98, y + 2, len * 0.7, 2);
  }
};

const paintHeightBase = (g: CanvasRenderingContext2D, w: number, h: number): void => {
  g.fillStyle = '#9b9b9b';
  g.fillRect(0, 0, w, h);
  for (let i = 0; i < 240; i++) {
    const x = hashF(i * 5 + 0) * w;
    const y = hashF(i * 5 + 1) * h;
    const len = 24 + hashF(i * 5 + 2) * 120;
    const alpha = 0.012 + hashF(i * 5 + 3) * 0.02;
    g.fillStyle = `rgba(255,255,255,${alpha})`;
    g.fillRect(x, y, len, 1);
    g.fillStyle = `rgba(0,0,0,${alpha})`;
    g.fillRect(x + 8, y + 2, len * 0.55, 1);
  }
};

const paintNameRows = (
  colorCtx: CanvasRenderingContext2D,
  heightCtx: CanvasRenderingContext2D,
  w: number,
  h: number,
  font: string,
): void => {
  colorCtx.font = `${INSCRIPTION_FONT_PX}px ${font}`;
  heightCtx.font = `${INSCRIPTION_FONT_PX}px ${font}`;
  colorCtx.textBaseline = 'top';
  heightCtx.textBaseline = 'top';
  for (let y = INSCRIPTION_MARGIN_Y; y < h; y += INSCRIPTION_ROW_STEP) {
    for (let x = INSCRIPTION_MARGIN_X; x < w; x += INSCRIPTION_COL_STEP) {
      const line = engravedNameAt(x, y);
      colorCtx.fillStyle = 'rgba(230,233,236,0.5)';
      colorCtx.fillText(line, x - 1, y - 1);
      colorCtx.fillStyle = 'rgba(18,20,22,0.86)';
      colorCtx.fillText(line, x + 2, y + 2);
      colorCtx.fillStyle = 'rgba(152,159,166,0.92)';
      colorCtx.fillText(line, x, y);
      heightCtx.fillStyle = 'rgba(255,255,255,0.38)';
      heightCtx.fillText(line, x - 1, y - 1);
      heightCtx.fillStyle = 'rgba(40,40,40,0.94)';
      heightCtx.fillText(line, x + 2, y + 2);
      heightCtx.fillStyle = 'rgba(112,112,112,0.82)';
      heightCtx.fillText(line, x, y);
    }
  }
};

const buildInscriptionAtlas = (): InscriptionAtlas => {
  const colorCanvas = document.createElement('canvas');
  colorCanvas.width = INSCRIPTION_SIZE;
  colorCanvas.height = INSCRIPTION_SIZE;
  const heightCanvas = document.createElement('canvas');
  heightCanvas.width = INSCRIPTION_SIZE;
  heightCanvas.height = INSCRIPTION_SIZE;
  const colorCtx = colorCanvas.getContext('2d') as CanvasRenderingContext2D;
  const heightCtx = heightCanvas.getContext('2d') as CanvasRenderingContext2D;
  const normalCanvas = document.createElement('canvas');
  normalCanvas.width = NORMAL_SIZE;
  normalCanvas.height = NORMAL_SIZE;
  const colorMap = new THREE.CanvasTexture(colorCanvas);
  const heightMap = new THREE.CanvasTexture(heightCanvas);
  const normalMap = new THREE.CanvasTexture(normalCanvas);
  colorMap.colorSpace = THREE.SRGBColorSpace;
  normalMap.colorSpace = THREE.LinearSRGBColorSpace; // normals are data, never sRGB
  for (const tex of [colorMap, heightMap, normalMap]) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.copy(INSCRIPTION_REPEAT);
    tex.anisotropy = 8;
  }
  return { colorCtx, heightCtx, colorMap, heightMap, normalCanvas, normalMap };
};

const redrawInscriptionAtlas = (atlas: InscriptionAtlas, font: string): void => {
  paintStoneBase(atlas.colorCtx, INSCRIPTION_SIZE, INSCRIPTION_SIZE);
  paintHeightBase(atlas.heightCtx, INSCRIPTION_SIZE, INSCRIPTION_SIZE);
  paintNameRows(atlas.colorCtx, atlas.heightCtx, INSCRIPTION_SIZE, INSCRIPTION_SIZE, font);
  // Derive the normal map from the freshly-painted height field (gameplay relief).
  computeNormalMap(atlas.heightCtx.canvas, atlas.normalCanvas, NORMAL_STRENGTH);
  atlas.colorMap.needsUpdate = true;
  atlas.heightMap.needsUpdate = true;
  atlas.normalMap.needsUpdate = true;
};

const buildStoneMaterial = (atlas: InscriptionAtlas): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({
    map: atlas.colorMap,
    bumpMap: atlas.heightMap,
    bumpScale: STONE_BUMP_SCALE,
    // Wave 2b: real tangent-space relief for gameplay (nulled in the cinematic path).
    normalMap: atlas.normalMap,
    normalScale: new THREE.Vector2(STONE_NORMAL_SCALE, STONE_NORMAL_SCALE),
    color: 0xffffff,
    metalness: STONE_METALNESS,
    roughness: STONE_ROUGHNESS,
    envMapIntensity: 0.72,
  });

const inscribeMaterial = (material: THREE.Material, atlas: InscriptionAtlas): THREE.Material => {
  if (!(material instanceof THREE.MeshStandardMaterial)) return material;
  material.map = atlas.colorMap;
  material.bumpMap = atlas.heightMap;
  material.bumpScale = STONE_BUMP_SCALE;
  // Gameplay hero GLB gains the same derived normal map (was null before Wave 2b).
  material.normalMap = atlas.normalMap;
  material.normalScale = new THREE.Vector2(STONE_NORMAL_SCALE, STONE_NORMAL_SCALE);
  material.color.setHex(0xffffff);
  material.metalness = STONE_METALNESS;
  material.roughness = STONE_ROUGHNESS;
  material.envMapIntensity = 0.72;
  material.emissive.setHex(0x000000);
  material.emissiveIntensity = 0;
  material.needsUpdate = true;
  return material;
};

const applyInscriptions = (root: THREE.Object3D, atlas: InscriptionAtlas): void => {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    if (Array.isArray(o.material)) {
      o.material = o.material.map((mat) => inscribeMaterial(mat, atlas));
      return;
    }
    o.material = inscribeMaterial(o.material, atlas);
  });
};

export class CenotaphRenderer {
  private readonly group = new THREE.Group();
  private readonly fallback: THREE.Mesh;
  private readonly inscription: InscriptionAtlas;
  /** Memorial ring (equatorial plane, counter-rotates vs the monument body). */
  private readonly memorialRing: THREE.Mesh;
  /** Candle-motes (sparse deterministic particle layer). */
  private readonly motes: THREE.Points;
  private readonly motesGeo: THREE.BufferGeometry;
  /**
   * Low-poly sphere proxy for mid-range LOD (hero GLB hidden, proxy visible).
   * Sized to RADIUS_KM so the silhouette swap is not jarring.
   */
  private readonly heroProxy: THREE.Mesh;
  /** The loaded hero GLB scene, or null until/unless the GLB loads. */
  private heroGlb: THREE.Object3D | null = null;

  // --- Cinematic (intro) state — all inert unless setCinematicMaterial(true). ---
  private cinematic = false;
  private readonly cineStores = new Map<THREE.MeshStandardMaterial, CineMatStore>();
  /** Shared rim uniforms — one set wired into every cinematic stone material. */
  private readonly rimUniforms = {
    uSunDirView: { value: new THREE.Vector3(0, 0, 1) },
    uRimColor: { value: new THREE.Vector3(0.812, 0.902, 0.933) }, // rgb(207,230,238)
    uRimStrength: { value: 0.85 },
    uRimPower: { value: 3.0 }, // hairline crescent
  };
  private ringStore: { emissiveIntensity: number; opacity: number } | null = null;
  private moteStore: { opacity: number } | null = null;
  /** dec→HQ crossfade clock anchor (<0 idle); set when the HQ GLB lands during the cinematic. */
  private glbSwapStart = -1;
  private glbSwapReady = false;

  constructor(parent: THREE.Group) {
    this.inscription = buildInscriptionAtlas();
    redrawInscriptionAtlas(this.inscription, FALLBACK);
    this.fallback = new THREE.Mesh(
      new THREE.SphereGeometry(RADIUS_KM, 64, 48),
      buildStoneMaterial(this.inscription),
    );
    this.group.add(this.fallback);
    this.memorialRing = buildMemorialRing();
    this.group.add(this.memorialRing);
    this.motesGeo = new THREE.BufferGeometry();
    this.motes = buildMotes(this.motesGeo);
    this.group.add(this.motes);
    this.heroProxy = buildHeroProxy();
    this.heroProxy.visible = false; // hidden until LOD activates it
    this.group.add(this.heroProxy);

    this.group.visible = false;
    parent.add(this.group);
    void this.loadWallFont();
    void this.loadHeroGlb();
  }

  /** AAA-10: load the high-quality generated hero mesh; on success it replaces the
   * procedural fallback. On any failure the fallback sphere remains. */
  private async loadHeroGlb(url = cenotaphGlbUrl()): Promise<void> {
    try {
      const gltf = await createGltfLoader().loadAsync(url);
      normaliseToRadius(gltf.scene, RADIUS_KM);
      applyInscriptions(gltf.scene, this.inscription);
      this.group.add(gltf.scene);
      this.heroGlb = gltf.scene;
      if (this.cinematic) {
        // Cinematic: keep the proxy until updateCinematic() crossfades HQ in over 0.5s
        // (a silent dec→HQ opacity hot-swap), rather than a hard pop mid-rise.
        eachStoneMaterial(gltf.scene, (m) => this.applyCineMat(m));
        this.glbSwapReady = true;
      } else {
        this.fallback.visible = false; // gameplay: hero asset present — drop the procedural sphere
      }
    } catch {
      /* keep the procedural fallback sphere */
    }
  }

  /** AAA-08: async-load the stonemason serif, then redraw the inscription with it. */
  private async loadWallFont(): Promise<void> {
    try {
      const face = new FontFace(WALL_FONT, "url('/assets/fonts/Cinzel-Regular.woff2') format('woff2')");
      await face.load();
      (document as Document & { fonts: FontFaceSet }).fonts.add(face);
      redrawInscriptionAtlas(this.inscription, `${WALL_FONT}, ${FALLBACK}`);
    } catch {
      /* keep the fallback serif */
    }
  }

  /** Place the Cenotaph at `centre` (floating-origin), or hide it when null. */
  update(centre: Vec3 | null, shipWorldPos: Vec3, simTime: number): void {
    if (!centre) {
      this.group.visible = false;
      return;
    }
    this.group.visible = true;
    this.group.position.set(
      centre.x - shipWorldPos.x,
      centre.y - shipWorldPos.y,
      centre.z - shipWorldPos.z,
    );
    this.group.rotation.y = simTime * 0.01; // Monument body slow rotation (reverent).

    // Ring counter-rotates vs the group so it appears to drift opposite the sphere.
    this.memorialRing.rotation.y = simTime * RING_ROT_RATE - this.group.rotation.y;

    this.updateMotes(simTime);

    // LOD: toggle hero GLB vs proxy.  Procedural fallback is never LOD-toggled here.
    if (this.heroGlb !== null) {
      applyLod(this.group.position, this.heroGlb, this.heroProxy, FULL_CENOTAPH_KM, PROXY_CENOTAPH_KM);
    }
  }

  private updateMotes(simTime: number): void {
    const pos = this.motesGeo.getAttribute('position') as THREE.BufferAttribute;
    for (let mi = 0; mi < MOTE_COUNT; mi++) {
      const baseAngle = hashF(mi * 3 + 0) * Math.PI * 2;
      const radius = RING_INNER + hashF(mi * 3 + 1) * (RING_OUTER - RING_INNER);
      const height = (hashF(mi * 3 + 2) - 0.5) * RADIUS_KM * 0.5;
      const driftAngle = baseAngle + simTime * (MOTE_DRIFT_BASE * (1 + mi % 5));
      pos.setXYZ(mi, Math.cos(driftAngle) * radius, height, Math.sin(driftAngle) * radius);
    }
    pos.needsUpdate = true;
  }

  // =========================================================================
  // CINEMATIC (intro) render path — gated behind setCinematicMaterial(true).
  // Gameplay NEVER calls these; the gameplay material/light path is byte-identical.
  // =========================================================================

  /** The monument group (so the intro scene can frame the real object at origin). */
  get object3d(): THREE.Group {
    return this.group;
  }

  /**
   * Toggle the matte-obsidian cinematic override + Fresnel rim crescent. Stores every
   * gameplay value before override and restores it verbatim when cleared — so a handoff
   * back to gameplay sees the original STONE_* lighting. The memorial ring (the one
   * sanctioned cyan accent) keeps its colour but gets emissive/opacity nudges since
   * bloom is gone; candle-motes lift slightly. Idempotent.
   */
  setCinematicMaterial(on: boolean): void {
    if (on === this.cinematic) return;
    this.cinematic = on;
    if (on) this.group.visible = true;
    const apply = (m: THREE.MeshStandardMaterial): void =>
      on ? this.applyCineMat(m) : this.restoreCineMat(m);
    eachStoneMaterial(this.fallback, apply);
    eachStoneMaterial(this.heroProxy, apply);
    if (this.heroGlb) eachStoneMaterial(this.heroGlb, apply);
    this.gradeRingAndMotes(on);
  }

  /** Drive the entire cinematic body off the CineClock `t` (deterministic). */
  updateCinematic(t: number, camera: THREE.PerspectiveCamera): void {
    this.group.rotation.y = CINE_ROT_PHASE + t * CINE_ROT_RATE;
    this.memorialRing.rotation.y = t * RING_ROT_RATE - this.group.rotation.y;
    this.updateMotes(t);
    this.rimUniforms.uSunDirView.value.copy(SUN_DIR).transformDirection(camera.matrixWorldInverse);
    this.rimUniforms.uRimStrength.value = introRimStrength(t);
    this.tickGlbSwap(t);
  }

  private applyCineMat(m: THREE.MeshStandardMaterial): void {
    if (!this.cineStores.has(m)) {
      this.cineStores.set(m, {
        metalness: m.metalness,
        roughness: m.roughness,
        envMapIntensity: m.envMapIntensity,
        color: m.color.getHex(),
        bumpScale: m.bumpScale,
        emissive: m.emissive.getHex(),
        emissiveIntensity: m.emissiveIntensity,
        normalMap: m.normalMap,
        onBeforeCompile: m.onBeforeCompile,
      });
    }
    m.metalness = CINE_METALNESS;
    m.roughness = CINE_ROUGHNESS;
    m.envMapIntensity = CINE_ENVMAP_INTENSITY;
    m.color.setHex(CINE_ALBEDO);
    m.bumpScale = CINE_BUMP_SCALE;
    // Cinematic uses the bump-only relief (as pre-Wave-2b): drop the normal map so
    // the raking key throws the exact same micro-shadows the calibrated intro shipped.
    m.normalMap = null;
    m.emissive.setHex(0x000000);
    m.emissiveIntensity = 0;
    m.onBeforeCompile = (shader): void => {
      shader.uniforms.uSunDirView = this.rimUniforms.uSunDirView;
      shader.uniforms.uRimColor = this.rimUniforms.uRimColor;
      shader.uniforms.uRimStrength = this.rimUniforms.uRimStrength;
      shader.uniforms.uRimPower = this.rimUniforms.uRimPower;
      shader.fragmentShader =
        'uniform vec3 uSunDirView;\nuniform vec3 uRimColor;\nuniform float uRimStrength;\nuniform float uRimPower;\n' +
        shader.fragmentShader.replace(
          '#include <opaque_fragment>',
          `${RIM_GLSL}\n#include <opaque_fragment>`,
        );
    };
    m.needsUpdate = true;
  }

  private restoreCineMat(m: THREE.MeshStandardMaterial): void {
    const s = this.cineStores.get(m);
    if (!s) return;
    m.metalness = s.metalness;
    m.roughness = s.roughness;
    m.envMapIntensity = s.envMapIntensity;
    m.color.setHex(s.color);
    m.bumpScale = s.bumpScale;
    m.emissive.setHex(s.emissive);
    m.emissiveIntensity = s.emissiveIntensity;
    m.normalMap = s.normalMap; // restore the gameplay relief on handoff to gameplay
    m.onBeforeCompile = s.onBeforeCompile;
    m.needsUpdate = true;
    this.cineStores.delete(m);
  }

  private gradeRingAndMotes(on: boolean): void {
    const ring = this.memorialRing.material as THREE.MeshStandardMaterial;
    const moteMat = this.motes.material as THREE.PointsMaterial;
    if (on) {
      this.ringStore = { emissiveIntensity: ring.emissiveIntensity, opacity: ring.opacity };
      this.moteStore = { opacity: moteMat.opacity };
      // The reference cinematic is the BARE black marble — no Saturn-style ring, no
      // orbiting candle-motes. They belong to the in-game encounter, not the title film.
      this.memorialRing.visible = false;
      this.motes.visible = false;
    } else {
      this.memorialRing.visible = true;
      this.motes.visible = true;
      if (this.ringStore) {
        ring.emissiveIntensity = this.ringStore.emissiveIntensity;
        ring.opacity = this.ringStore.opacity;
        this.ringStore = null;
      }
      if (this.moteStore) {
        moteMat.opacity = this.moteStore.opacity;
        this.moteStore = null;
      }
    }
  }

  /** Crossfade the HQ GLB in over 0.5s once it lands (the dec→HQ opacity hot-swap). */
  private tickGlbSwap(t: number): void {
    if (!this.glbSwapReady || !this.heroGlb) return;
    if (this.glbSwapStart < 0) {
      this.glbSwapStart = t;
      setObjectOpacity(this.fallback, 1, true);
      setObjectOpacity(this.heroGlb, 0, true);
      this.heroGlb.visible = true;
    }
    const k = clamp((t - this.glbSwapStart) / 0.5, 0, 1);
    setObjectOpacity(this.fallback, 1 - k, true);
    setObjectOpacity(this.heroGlb, k, true);
    if (k >= 1) {
      this.fallback.visible = false;
      setObjectOpacity(this.heroGlb, 1, false); // restore opaque blending on the hero
      this.glbSwapReady = false;
    }
  }
}

/** Build the equatorial memorial ring. */
function buildMemorialRing(): THREE.Mesh {
  const geo = new THREE.RingGeometry(RING_INNER, RING_OUTER, 96, 2);
  geo.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
  const mat = new THREE.MeshStandardMaterial({
    color: 0x8ecfdb,
    emissive: 0x1a3840,
    emissiveIntensity: 0.35,
    roughness: 0.85,
    metalness: 0.25,
    transparent: true,
    opacity: 0.72,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(geo, mat);
  ring.renderOrder = 1;
  return ring;
}

/** Build the candle-mote points layer (deterministic seed). */
function buildMotes(motesGeo: THREE.BufferGeometry): THREE.Points {
  const motePositions = new Float32Array(MOTE_COUNT * 3);
  for (let mi = 0; mi < MOTE_COUNT; mi++) {
    const angle = hashF(mi * 3 + 0) * Math.PI * 2;
    const radius = RING_INNER + hashF(mi * 3 + 1) * (RING_OUTER - RING_INNER);
    const height = (hashF(mi * 3 + 2) - 0.5) * RADIUS_KM * 0.5;
    motePositions[mi * 3]     = Math.cos(angle) * radius;
    motePositions[mi * 3 + 1] = height;
    motePositions[mi * 3 + 2] = Math.sin(angle) * radius;
  }
  motesGeo.setAttribute('position', new THREE.BufferAttribute(motePositions.slice(), 3));
  const mat = new THREE.PointsMaterial({
    color: 0xaedde8,
    size: RADIUS_KM * 0.025,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });
  const pts = new THREE.Points(motesGeo, mat);
  pts.renderOrder = 2;
  return pts;
}

/** Low-poly sphere proxy for mid-range LOD of the hero GLB. */
function buildHeroProxy(): THREE.Mesh {
  // 8×6 segments — cheap silhouette, same radius as the hero asset.
  const geo = new THREE.SphereGeometry(RADIUS_KM, 8, 6);
  const mat = new THREE.MeshStandardMaterial({ color: 0x7b8287, roughness: 0.82, metalness: 0.42 });
  return new THREE.Mesh(geo, mat);
}

/** Visit every MeshStandardMaterial under `root` (handles multi-material meshes). */
function eachStoneMaterial(
  root: THREE.Object3D,
  fn: (m: THREE.MeshStandardMaterial) => void,
): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    for (const mat of mats) if (mat instanceof THREE.MeshStandardMaterial) fn(mat);
  });
}

/** Set transparent + opacity on every material under `root` (for the dec→HQ crossfade). */
function setObjectOpacity(root: THREE.Object3D, opacity: number, transparent: boolean): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    for (const mat of mats) {
      if (mat.transparent !== transparent) {
        mat.transparent = transparent;
        mat.needsUpdate = true;
      }
      mat.opacity = opacity;
    }
  });
}

/**
 * Foreground PAPER-ASH mote layer (Beat-3 intimacy). Distinct from the cyan ring
 * candle-motes — warm paper ash drifting between camera and monument, so the project's
 * "one cyan accent" rule holds. Parented to the cinematic camera (screen-space-ish):
 * 34 motes, depth-graded drift speed (near faster → real parallax), faded in 11.0→12.8
 * and out 18.5→20.0 to match the carve/rake cues. Driven entirely off the CineClock —
 * gameplay never instantiates it, so it cannot touch the in-game render path.
 */
export class AshMoteLayer {
  readonly points: THREE.Points;
  private readonly geo = new THREE.BufferGeometry();
  private readonly base: Float32Array;
  private readonly depth: Float32Array;
  private readonly mat: THREE.PointsMaterial;

  constructor() {
    const pos = new Float32Array(ASH_COUNT * 3);
    this.base = new Float32Array(ASH_COUNT * 3);
    this.depth = new Float32Array(ASH_COUNT);
    for (let i = 0; i < ASH_COUNT; i++) {
      const d = hashF(i * 7 + 1);
      const z = -2 - d * 26; // 2..28 km in front of the camera (it looks down −Z)
      const spread = Math.abs(z) * 0.6;
      const x = (hashF(i * 7 + 2) - 0.5) * 2 * spread;
      const y = (hashF(i * 7 + 3) - 0.5) * 2 * spread;
      this.base[i * 3] = x;
      this.base[i * 3 + 1] = y;
      this.base[i * 3 + 2] = z;
      this.depth[i] = d;
      pos[i * 3] = x;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = z;
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.mat = new THREE.PointsMaterial({
      color: 0xe8e2d4, // rgb(232,226,212) paper ash
      // Constant SCREEN-pixel size: these motes sit only 2–28 km from the camera, so
      // sizeAttenuation:true blew them up to 40–540 px squares that filled the frame.
      size: 2.6,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 5;
    this.points.visible = false;
  }

  /** Drift + fade the ash off the cinematic clock `t`. */
  update(t: number): void {
    const env = interpolate([11, 12.8, 18.5, 20], [0, 1, 1, 0], t, easeInOutSine);
    this.mat.opacity = env * 0.68;
    if (env <= 1e-3) {
      this.points.visible = false;
      return;
    }
    this.points.visible = true;
    const pos = this.geo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < ASH_COUNT; i++) {
      const z = this.base[i * 3 + 2]!;
      const spread = Math.abs(z) * 0.6;
      const range = spread * 2;
      const d = this.depth[i]!;
      const speed = 0.9 * (0.5 + 1.4 * d); // depth-scaled — near motes drift faster
      let y = this.base[i * 3 + 1]! + t * speed * 0.5; // upward rise
      y = (((y + spread) % range) + range) % range - spread; // wrap into [−spread, spread]
      const sway = Math.sin(t * 0.6 + d * Math.PI * 2) * spread * 0.06; // air-current drift
      pos.setXYZ(i, this.base[i * 3]! + sway, y, z);
    }
    pos.needsUpdate = true;
  }

  dispose(): void {
    this.geo.dispose();
    this.mat.dispose();
  }
}
